import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { TextFieldTheme } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import { useState, useEffect, useCallback } from "react";
import { useSubmitSuccessEvent } from "../../contexts/events-context.js";
import { useForcedState, ForcedState } from "../../contexts/forced-state-context.js";
import { useTranslation } from "../../contexts/translation-context.js";
import { useFieldAttributes } from "../../services/field-attributes.js";
import { FieldDescriptionWUT } from "../field-description/field-description-wut.js";
import { FieldErrorWUT } from "../field-error/field-error-wut.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { useInvokeCallback } from "../shared/use-invoke-callback.js";
import { Widget } from "../widget/widget.js";
import { parseLocalPhone, parseCountryCodeFromCallingCode } from "./phone-input/phone-input-utils.js";
import { classes } from "./phone-input-field-wut.st.css";
import { PhoneInputWUT } from "./phone-input/phone-input-wut/phone-input-wut.js";
const PhoneInputFieldWUT = ({
  id,
  target,
  label,
  hideLabel = false,
  placeholder,
  required = false,
  description,
  value = "",
  defaultCountry,
  showFlag,
  validation,
  onChange: _onChange,
  onBlur,
  onFocus,
  error,
  disabled = false,
  hasError = false,
  errorMessage,
  FieldLayout,
  requiredIndicatorType,
  requiredIndicatorPlacement,
  readOnly,
  defaultValue,
  widgetHtml,
  widgetEnabled
}) => {
  const { t } = useTranslation();
  const { labelId, inputId, errorId, descriptionId, ariaDescribedBy } = useFieldAttributes(id, error, description);
  const allowedCountryCodes = validation?.string?.phoneOptions?.allowedCountryCodes;
  const getInitialCurrentValue = () => {
    if (defaultValue === value && defaultValue.length <= 4 && !!parseCountryCodeFromCallingCode(defaultValue)) {
      return "";
    }
    return value;
  };
  const [currentValue, setCurrentValue] = useState(getInitialCurrentValue);
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  useEffect(() => {
    if (currentValue !== value) {
      _onChange(currentValue);
    }
  }, []);
  useEffect(() => {
    if (value && !currentValue) {
      setCurrentValue(value);
    }
  }, [value, currentValue]);
  const onChange = useCallback(
    (newValue, selectedCountry) => {
      const allowedCountry = allowedCountryCodes?.length === 1 ? allowedCountryCodes[0] : void 0;
      const country = selectedCountry || allowedCountry;
      const parsedValue = parseLocalPhone(newValue, country);
      setCurrentValue(newValue);
      _onChange(parsedValue);
    },
    [_onChange, allowedCountryCodes]
  );
  const invokeBlur = useInvokeCallback(onBlur);
  const handleBlur = useCallback(() => {
    invokeBlur();
  }, [invokeBlur]);
  const onSubmitSuccess = useCallback(() => {
    setCurrentValue("");
    _onChange("");
  }, [_onChange]);
  useSubmitSuccessEvent(onSubmitSuccess);
  return /* @__PURE__ */ jsx(
    FieldLayout,
    {
      fieldId: id,
      renderLabel: () => /* @__PURE__ */ jsx(
        FieldLabelWUT,
        {
          labelId,
          htmlFor: inputId,
          required,
          label,
          hideLabel,
          requiredIndicatorType,
          requiredIndicatorPlacement
        }
      ),
      renderInput: () => widgetHtml && widgetEnabled ? /* @__PURE__ */ jsx(
        Widget,
        {
          fieldId: id,
          widgetHtml,
          onChange: _onChange,
          value
        }
      ) : /* @__PURE__ */ jsx(
        PhoneInputWUT,
        {
          fieldId: id,
          inputId,
          hasError,
          value: currentValue ? currentValue : "",
          className: classNames(classes.textField, classes.theme, {
            [classes.error]: hasError,
            [classes.hover]: forcedHover
          }),
          onChange,
          required,
          disabled,
          placeholder,
          onBlur: handleBlur,
          onFocus,
          theme: TextFieldTheme.Box,
          ariaDescribedBy,
          ariaInvalid: hasError,
          showFlag,
          defaultCountry,
          allowedCountryCodes,
          readOnly,
          ariaLabel: `${label}. ${t("field.phone.aria-label")}`
        }
      ),
      renderDescription: () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          FieldErrorWUT,
          {
            target,
            hasError,
            errorMessage,
            id: errorId
          }
        ),
        /* @__PURE__ */ jsx(FieldDescriptionWUT, { description, id: descriptionId })
      ] })
    }
  );
};
export {
  PhoneInputFieldWUT
};
//# sourceMappingURL=phone-input-field-wut.js.map
